import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Github, Package, Terminal } from "lucide-react";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { COMPANY_NAME } from "@/lib/constants";

const NPM_URL = "https://www.npmjs.com/package/agentmrr";
const REPO_URL = "https://github.com/thijssmudde/agentmrr-cli";

export const Route = createFileRoute("/agent")({
	component: AgentPage,
	head: () => ({
		meta: [
			{
				title: `${COMPANY_NAME} for AI agents: the amrr CLI`,
			},
			{
				name: "description",
				content:
					"Use AgentMRR as your verified-revenue data layer. The dependency-free amrr CLI looks up any agent's verified MRR, all-time revenue and leaderboard rank, discovers top agents, and monitors your own listings. Free accounts include 100 API calls per month.",
			},
		],
	}),
});

const QUICKSTART = `# Install the skill
npx skills add thijssmudde/agentmrr-cli

# Install the CLI
npm install -g agentmrr

# Set your API key (free in your dashboard)
export AGENTMRR_API_KEY=amrr_your_key

# Top agents by MRR
amrr leaderboard:list

# Look up any agent's verified MRR
amrr agent:lookup acme`;

const PUBLIC_COMMANDS: Array<[string, string]> = [
	[
		"agent:lookup <agent>",
		"Verified MRR, all-time revenue, rank, verification",
	],
	["discover:find [filters]", "Discover agents, ranked by MRR"],
	["leaderboard:list", "The public MRR leaderboard"],
	["badge:snippets <agent>", "Badge / embed snippets"],
	["categories:list", "Valid category values"],
];

const OWNER_COMMANDS: Array<[string, string]> = [
	["agents:list", "List your agents + metrics"],
	["agents:get <agent>", "One of your agents with rank + authority"],
	["agents:export <agent>", "Machine-readable export of your agent"],
	["agents:monitor [<agent>]", "Watch MRR/revenue changes + alerts"],
	["agents:submit <website>", "List a new agent"],
];

function AgentPage() {
	return (
		<PublicPageLayout>
			<PublicHeader />
			<main className="flex flex-1 flex-col pt-14">
				<div className="mx-auto w-full max-w-5xl px-4 sm:px-6">
					{/* Hero */}
					<section className="pt-16 pb-12">
						<span className="inline-flex items-center gap-1.5 border border-brand/40 px-2.5 py-1 font-mono text-sm uppercase tracking-[0.14em] text-brand">
							<Terminal className="size-3.5 shrink-0" />
							AgentMRR for AI agents
						</span>
						<h1
							className="mt-5 max-w-3xl text-balance font-mono text-[clamp(1.6rem,4vw,2.75rem)] leading-[1.05] tracking-[-0.03em] text-foreground"
							style={{ fontWeight: 400 }}
						>
							Verified MRR data, one command away
						</h1>
						<p className="mt-5 max-w-2xl text-pretty text-base leading-[1.6] text-foreground/80">
							The <span className="text-brand">amrr</span> CLI is a tiny,
							dependency-free HTTP client for the AgentMRR API. Look up any
							agent's verified MRR, all-time revenue and leaderboard rank,
							discover top agents, grab badge snippets, and monitor your own
							listings. Clean JSON for scripts, CI, dashboards, and AI agents.
						</p>
						<p className="mt-3 max-w-2xl text-sm leading-[1.6] text-muted-foreground">
							Connecting other tools to your agent? Review the{" "}
							<Link
								href="/blog/mcp-server-security-checklist"
								className="text-foreground underline underline-offset-4 transition-colors hover:text-brand"
							>
								MCP server security checklist
							</Link>{" "}
							before you grant access to files, accounts, or live data.
						</p>
						<div className="mt-7 flex flex-wrap items-center gap-3">
							<a
								href={NPM_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex min-h-10 items-center gap-2 bg-foreground px-4 font-mono text-sm uppercase tracking-[0.14em] text-background transition-[background-color,transform] hover:bg-foreground/90 active:scale-[0.97]"
							>
								<Package className="size-3.5" />
								npm: agentmrr
							</a>
							<a
								href={REPO_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex min-h-10 items-center gap-2 border border-border px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:border-brand/40 hover:text-brand"
							>
								<Github className="size-3.5" />
								Source + skill
							</a>
							<Link
								to="/docs"
								className="inline-flex min-h-10 items-center gap-2 px-2 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-brand"
							>
								Read the docs
								<ArrowRight className="size-3.5" />
							</Link>
						</div>
					</section>

					{/* Quickstart terminal card */}
					<section className="pb-14">
						<div className="overflow-hidden border border-border bg-muted/20">
							<div className="flex items-center gap-2 border-b border-border bg-background/60 px-4 py-2.5">
								<span className="size-3 rounded-full bg-[color:var(--muted-foreground)]/40" />
								<span className="size-3 rounded-full bg-[color:var(--muted-foreground)]/40" />
								<span className="size-3 rounded-full bg-[color:var(--muted-foreground)]/40" />
								<span className="ml-2 font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground">
									quickstart
								</span>
							</div>
							<pre className="overflow-x-auto p-5 font-mono text-sm leading-[1.7] text-foreground/90">
								<code>{QUICKSTART}</code>
							</pre>
						</div>
						<p className="mt-3 font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground">
							Free tier: 100 API calls / month · Growth raises the limit ·
							Node.js ≥ 18
						</p>
					</section>

					{/* Command reference */}
					<section className="grid gap-10 pb-16 @2xl/page:grid-cols-2">
						<CommandColumn
							label="Public discovery: any listed agent"
							commands={PUBLIC_COMMANDS}
						/>
						<CommandColumn
							label="Your own agents: owner-scoped"
							commands={OWNER_COMMANDS}
						/>
					</section>
				</div>
			</main>
			<PublicFooter />
		</PublicPageLayout>
	);
}

function CommandColumn({
	label,
	commands,
}: {
	label: string;
	commands: Array<[string, string]>;
}) {
	return (
		<div>
			<h2 className="font-mono text-sm uppercase tracking-[0.14em] text-foreground">
				<span className="text-brand">&gt;</span> {label}
			</h2>
			<div className="mt-4 flex flex-col">
				{commands.map(([cmd, desc]) => (
					<div
						key={cmd}
						className="flex flex-col gap-1 border-b border-border py-3 last:border-b-0"
					>
						<code className="font-mono text-sm text-foreground">
							<span className="text-muted-foreground">amrr </span>
							{cmd}
						</code>
						<span className="text-sm leading-[1.5] text-muted-foreground">
							{desc}
						</span>
					</div>
				))}
			</div>
		</div>
	);
}
